import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGetData,
  type GetArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGetError,
  type GetArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGetResponse,
  getArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGet,
} from '../../generated';

export type GetArtifactQueryVariables =
  GetArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGetData['path'] & {
    organization_id: string;
  };

export type GetArtifactQuery = GetArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGetResponse;

export const useGetArtifactQuery = <
  TData = GetArtifactQuery,
  TError = GetArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGetError,
>(
  { organization_id, portfolio_id, image_id, artifact_type }: GetArtifactQueryVariables,
  options?: Omit<UseQueryOptions<GetArtifactQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetArtifactQuery, TError, TData>({
    queryKey: ['GetArtifact', { organization_id, portfolio_id, image_id, artifact_type }],
    queryFn: async () => {
      const { data } = await getArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGet({
        path: { portfolio_id, image_id, artifact_type },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetArtifactQuery.getKey = ({
  organization_id,
  portfolio_id,
  image_id,
  artifact_type,
}: GetArtifactQueryVariables) => ['GetArtifact', { organization_id, portfolio_id, image_id, artifact_type }];

useGetArtifactQuery.fetcher = (
  { organization_id: _organization_id, portfolio_id, image_id, artifact_type }: GetArtifactQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getArtifactV1PortfoliosPortfolioIdAssetsImagesImageIdArtifactsArtifactTypeGet({
      path: { portfolio_id, image_id, artifact_type },
      throwOnError: true,
    });
    return data;
  };
};
